import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  EducationAnnualPlan,
  EducationAnnualPlanAttachment,
  EducationAnnualPlanPayload,
  EducationRecord,
  EducationRecordAttachment,
  EducationRecordPayload,
  ExternalEducationReport,
  ExternalEducationReportAttachment,
  ExternalEducationReportPayload,
} from '../types/education';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchEducationPlans(): Promise<EducationAnnualPlan[]> {
  const { data } = await apiClient.get<EducationAnnualPlan[]>('/education/annual-plans');
  return data;
}

export async function fetchEducationPlan(planId: number): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.get<EducationAnnualPlan>(`/education/annual-plans/${planId}`);
  return data;
}

export async function createEducationPlan(
  payload: EducationAnnualPlanPayload
): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.post<EducationAnnualPlan>('/education/annual-plans', payload);
  return data;
}

export async function updateEducationPlan(
  planId: number,
  payload: EducationAnnualPlanPayload
): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.patch<EducationAnnualPlan>(
    `/education/annual-plans/${planId}`,
    payload
  );
  return data;
}

export async function deleteEducationPlan(planId: number): Promise<void> {
  await apiClient.delete(`/education/annual-plans/${planId}`);
}

export async function approveEducationPlan(planId: number): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.post<EducationAnnualPlan>(
    `/education/annual-plans/${planId}/approve`
  );
  return data;
}

export async function revokeEducationPlan(planId: number): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.post<EducationAnnualPlan>(
    `/education/annual-plans/${planId}/revoke`
  );
  return data;
}

export async function confirmEducationPlan(planId: number): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.post<EducationAnnualPlan>(
    `/education/annual-plans/${planId}/confirm`
  );
  return data;
}

export async function reopenEducationPlan(planId: number): Promise<EducationAnnualPlan> {
  const { data } = await apiClient.post<EducationAnnualPlan>(
    `/education/annual-plans/${planId}/reopen`
  );
  return data;
}

export async function uploadEducationPlanAttachments(
  planId: number,
  files: File[]
): Promise<EducationAnnualPlanAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<EducationAnnualPlanAttachment[]>(
    `/education/annual-plans/${planId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteEducationPlanAttachment(
  planId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/education/annual-plans/${planId}/attachments/${attachmentId}`);
}

export async function downloadEducationPlanAttachment(
  planId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/education/annual-plans/${planId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function fetchEducationRecords(keyword?: string): Promise<EducationRecord[]> {
  const { data } = await apiClient.get<EducationRecord[]>('/education/records', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchEducationRecord(recordId: number): Promise<EducationRecord> {
  const { data } = await apiClient.get<EducationRecord>(`/education/records/${recordId}`);
  return data;
}

export async function fetchNextEducationRecordNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/education/records/suggestions/record-no'
  );
  return data;
}

export async function createEducationRecord(
  payload: EducationRecordPayload
): Promise<EducationRecord> {
  const { data } = await apiClient.post<EducationRecord>('/education/records', payload);
  return data;
}

export async function updateEducationRecord(
  recordId: number,
  payload: EducationRecordPayload
): Promise<EducationRecord> {
  const { data } = await apiClient.patch<EducationRecord>(
    `/education/records/${recordId}`,
    payload
  );
  return data;
}

export async function deleteEducationRecord(recordId: number): Promise<void> {
  await apiClient.delete(`/education/records/${recordId}`);
}

export async function approveEducationRecord(recordId: number): Promise<EducationRecord> {
  const { data } = await apiClient.post<EducationRecord>(`/education/records/${recordId}/approve`);
  return data;
}

export async function revokeEducationRecord(recordId: number): Promise<EducationRecord> {
  const { data } = await apiClient.post<EducationRecord>(`/education/records/${recordId}/revoke`);
  return data;
}

export async function confirmEducationRecord(recordId: number): Promise<EducationRecord> {
  const { data } = await apiClient.post<EducationRecord>(`/education/records/${recordId}/confirm`);
  return data;
}

export async function reopenEducationRecord(recordId: number): Promise<EducationRecord> {
  const { data } = await apiClient.post<EducationRecord>(`/education/records/${recordId}/reopen`);
  return data;
}

export async function uploadEducationRecordAttachments(
  recordId: number,
  files: File[]
): Promise<EducationRecordAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<EducationRecordAttachment[]>(
    `/education/records/${recordId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteEducationRecordAttachment(
  recordId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/education/records/${recordId}/attachments/${attachmentId}`);
}

export async function downloadEducationRecordAttachment(
  recordId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/education/records/${recordId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function fetchExternalEducationReports(
  keyword?: string
): Promise<ExternalEducationReport[]> {
  const { data } = await apiClient.get<ExternalEducationReport[]>('/education/external-reports', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchExternalEducationReport(
  reportId: number
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.get<ExternalEducationReport>(
    `/education/external-reports/${reportId}`
  );
  return data;
}

export async function fetchNextExternalEducationReportNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/education/external-reports/suggestions/report-no'
  );
  return data;
}

export async function createExternalEducationReport(
  payload: ExternalEducationReportPayload
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.post<ExternalEducationReport>(
    '/education/external-reports',
    payload
  );
  return data;
}

export async function updateExternalEducationReport(
  reportId: number,
  payload: ExternalEducationReportPayload
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.patch<ExternalEducationReport>(
    `/education/external-reports/${reportId}`,
    payload
  );
  return data;
}

export async function deleteExternalEducationReport(reportId: number): Promise<void> {
  await apiClient.delete(`/education/external-reports/${reportId}`);
}

export async function approveExternalEducationReport(
  reportId: number
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.post<ExternalEducationReport>(
    `/education/external-reports/${reportId}/approve`
  );
  return data;
}

export async function revokeExternalEducationReport(
  reportId: number
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.post<ExternalEducationReport>(
    `/education/external-reports/${reportId}/revoke`
  );
  return data;
}

export async function confirmExternalEducationReport(
  reportId: number
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.post<ExternalEducationReport>(
    `/education/external-reports/${reportId}/confirm`
  );
  return data;
}

export async function reopenExternalEducationReport(
  reportId: number
): Promise<ExternalEducationReport> {
  const { data } = await apiClient.post<ExternalEducationReport>(
    `/education/external-reports/${reportId}/reopen`
  );
  return data;
}

export async function uploadExternalEducationReportAttachments(
  reportId: number,
  files: File[]
): Promise<ExternalEducationReportAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<ExternalEducationReportAttachment[]>(
    `/education/external-reports/${reportId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteExternalEducationReportAttachment(
  reportId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/education/external-reports/${reportId}/attachments/${attachmentId}`);
}

export async function downloadExternalEducationReportAttachment(
  reportId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/education/external-reports/${reportId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadEducationAnnualPlanPdf(
  planId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/education/annual-plans/${planId}/pdf`,
    fileName || `연간교육계획_${planId}.pdf`
  );
}

export async function downloadEducationRecordPdf(
  recordId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/education/records/${recordId}/pdf`,
    fileName || `교육일지_${recordId}.pdf`
  );
}

export async function downloadExternalEducationReportPdf(
  reportId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/education/external-reports/${reportId}/pdf`,
    fileName || `외부교육보고서_${reportId}.pdf`
  );
}
